import React from 'react';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import {
  currencyUI,
  currencySchema,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
  fullNameUI,
  fullNameSchema,
  textUI,
  yesNoUI,
  yesNoSchema,
  radioUI,
  radioSchema,
  ssnUI,
  ssnSchema,
  checkboxUI,
  checkboxSchema,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  selectUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  STATE_NAMES,
  STATE_VALUES,
  COUNTRY_NAMES,
  COUNTRY_VALUES,
  childHouseholdLabels,
  childRelationshipLabels,
} from '../../../utils/labels';
import { customAddressSchema } from '../../definitions';
import {
  seriouslyDisabledDescription,
  isRelationshipToVeteran,
  formatFullName,
} from '../../../utils/helpers';
import content from '../../../utils/content.json';

const checkIfItemIncomplete = (item, fullData) => {
  const relationship = isRelationshipToVeteran(fullData);
  return (
    !item?.childFullName ||
    !item?.childDateOfBirth ||
    !item?.relationship ||
    (relationship === 'child' && !item?.childLivesWith) ||
    (relationship !== 'child' && item?.nonChildLivesWith === undefined)
  );
};

/**
 * Dependent children (array builder)
 */
/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'veteransChildren',
  nounSingular: content['household-dependents-array-noun-singular'],
  nounPlural: content['household-dependents-array-noun-plural'],
  required: false,
  maxItems: 3,
  isItemIncomplete: checkIfItemIncomplete,
  text: {
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['household-dependents-array-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['household-dependents-array-noun-singular']}?`,
    ),
    cancelAddDescription:
      content['household-dependents-array-cancel-add-description'],
    cancelAddReviewDescription:
      content['household-dependents-array-cancel-add-description'],
    cancelEditDescription:
      content['household-dependents-array-cancel-edit-description'],
    cancelEditReviewDescription:
      content['household-dependents-array-cancel-edit-description'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      `${content['household-dependents-array-noun-plural']}.`,
      `${content['household-dependents-array-noun-singular']}.`,
    ]),
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['household-dependents-array-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: (
      <div>
        <p className="vads-u-margin-top--0">
          You have added the maximum number of allowed dependent children for
          this application. Additional children can be added using an
          Application Request to Add and/or Remove Dependents (VA Form 21-686c).
        </p>
        <p>
          You can submit this form online, upload it at the end of this
          application, or send it to us by mail.
        </p>
        <span className="vads-u-display--block vads-u-margin-bottom--2">
          <va-link
            href="https://www.va.gov/manage-dependents/add-remove-form-21-686c-674"
            external
            text="Add or remove dependents on VA benefits"
          />
        </span>
        <span className="vads-u-display--block">
          <va-link
            href="https://www.va.gov/find-forms/about-form-21-686c/"
            external
            text="Get VA Form 21P-686c to download"
          />
        </span>
      </div>
    ),
    getItemName: item => {
      if (item && item.childFullName?.first && item.childFullName?.last) {
        return `${item.childFullName.first} ${item.childFullName.last}`;
      }
      return 'Dependent';
    },
    summaryTitle: content['household-dependents-array-summary-title'],
  },
};

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:isAddingDependent': arrayBuilderYesNoUI(
      options,
      {
        title: content['household-dependents-array-summary-add'],
        hint: content['array-empty-hint'],
      },
      {
        title: content['household-dependents-array-summary-add-another'],
        hint: content['array-empty-hint'],
      },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:isAddingDependent': arrayBuilderYesNoSchema,
    },
    required: ['view:isAddingDependent'],
  },
};

/** @returns {PageSchema} */
const namePage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['household-dependents-array-child-name-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    childFullName: fullNameUI(),
    childSocialSecurityNumber: {
      ...ssnUI(),
      'ui:required': (formData, index) => {
        const item = formData?.veteransChildren?.[index];
        const currentPageData = formData;
        return !(item?.noSsn || currentPageData?.noSsn);
      },

      'ui:options': {
        hideIf: (formData, index) => {
          const item = formData?.veteransChildren?.[index];
          const currentPageData = formData;
          return item?.noSsn || currentPageData?.noSsn;
        },
      },
    },
    noSsn: checkboxUI({
      title: content['household-dependents-array-child-name-no-ssn'],
    }),
  },
  schema: {
    type: 'object',
    properties: {
      childFullName: fullNameSchema,
      childSocialSecurityNumber: ssnSchema,
      noSsn: checkboxSchema,
    },
    required: ['childFullName'],
  },
};

/** @returns {PageSchema} */
const dobPlacePage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(({ formData }) => {
      return replaceStrValues(
        content['household-dependents-array-dob-place-title'],
        formData?.childFullName
          ? formatFullName(formData?.childFullName)
          : 'child',
      );
    }),
    childDateOfBirth: currentOrPastDateUI({
      title: content['household-dependents-array-dob-place-dob'],
      monthSelect: false,
      'ui:description': content['date-description'],
      required: formData => !formData['view:dateOfBirth'],
      errorMessages: {
        required: content['household-dependents-array-dob-place-dob-error'],
      },
    }),
    bornOutsideUs: checkboxUI({
      title: content['household-dependents-array-dob-place-born-outside-us'],
    }),
    birthPlace: {
      city: {
        ...textUI(content['address-city']),
        'ui:errorMessages': {
          required: content['address-city-error'],
        },
      },
      state: {
        ...selectUI(content['address-state'], STATE_VALUES, STATE_NAMES),
        'ui:required': (formData, index) => {
          const item = formData?.veteransChildren?.[index];
          const currentPageData = formData;
          return !(item?.bornOutsideUs || currentPageData?.bornOutsideUs);
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteransChildren?.[index];
            const currentPageData = formData;
            return item?.bornOutsideUs || currentPageData?.bornOutsideUs;
          },
        },
        'ui:errorMessages': {
          required: content['address-state-error'],
        },
      },
      otherCountry: {
        ...selectUI(content['address-country'], COUNTRY_VALUES, COUNTRY_NAMES),
        'ui:required': (formData, index) => {
          const item = formData?.veteransChildren?.[index];
          const currentPageData = formData;
          return item?.bornOutsideUs || currentPageData?.bornOutsideUs;
        },
        'ui:options': {
          hideIf: (formData, index) => {
            const item = formData?.veteransChildren?.[index];
            const currentPageData = formData;
            return !(item?.bornOutsideUs || currentPageData?.bornOutsideUs);
          },
          labels: COUNTRY_VALUES.reduce((acc, value, idx) => {
            acc[value] = COUNTRY_NAMES[idx];
            return acc;
          }, {}),
        },
        'ui:errorMessages': {
          required: content['address-country-error'],
        },
      },
    },
  },
  schema: {
    type: 'object',
    required: ['birthPlace', 'childDateOfBirth'],
    properties: {
      childDateOfBirth: currentOrPastDateSchema,
      bornOutsideUs: checkboxSchema,
      birthPlace: customAddressSchema,
    },
  },
};

/** @returns {PageSchema} */
const relationshipPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(({ formData }) => {
      return replaceStrValues(
        content['household-dependents-array-relationship-title'],
        formData?.childFullName
          ? formatFullName(formData?.childFullName)
          : 'child',
      );
    }),
    relationship: radioUI({
      title: content['household-dependents-array-relationship-question'],
      labels: childRelationshipLabels,
    }),
  },
  schema: {
    type: 'object',
    properties: {
      relationship: radioSchema(Object.keys(childRelationshipLabels)),
    },
    required: ['relationship'],
  },
};

const dependentInfoPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(({ formData }) => {
      return replaceStrValues(
        content['household-dependents-array-dependent-info-title'],
        formData?.childFullName
          ? formatFullName(formData?.childFullName)
          : 'child',
      );
    }),
    inSchool: yesNoUI({
      title: content['household-dependents-array-dependent-info-in-school'],
    }),
    seriouslyDisabled: yesNoUI({
      title:
        content['household-dependents-array-dependent-info-seriously-disabled'],
    }),
    seriouslyDisabledInfo: {
      'ui:description': () => <div>{seriouslyDisabledDescription}</div>,
    },
    hasBeenMarried: yesNoUI({
      title:
        content[
          'household-dependents-array-dependent-info-seriously-has-been-married'
        ],
    }),
    currentlyMarried: {
      ...yesNoUI({
        title:
          content[
            'household-dependents-array-dependent-info-currently-married'
          ],
      }),
      'ui:options': {
        expandUnder: 'hasBeenMarried',
        expandUnderCondition: true,
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      inSchool: yesNoSchema,
      seriouslyDisabled: yesNoSchema,
      seriouslyDisabledInfo: {
        type: 'object',
        properties: {},
      },
      hasBeenMarried: yesNoSchema,
      currentlyMarried: yesNoSchema,
    },
    required: ['inSchool', 'seriouslyDisabled', 'hasBeenMarried'],
  },
};

const householdPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(({ formData }) => {
      return replaceStrValues(
        content['household-dependents-array-household-title'],
        formData?.childFullName
          ? formatFullName(formData?.childFullName)
          : 'child',
      );
    }),
    nonChildLivesWith: yesNoUI({
      title: content['household-dependents-array-household-question'],
      required: (formData, _index, fullData) => {
        const data = formData?.claimantRelationship ? formData : fullData;
        return isRelationshipToVeteran(data) !== 'child';
      },
      hideIf: (formData, _index, fullData) => {
        const data = formData?.claimantRelationship ? formData : fullData;
        return isRelationshipToVeteran(data) === 'child';
      },
    }),
    childLivesWith: radioUI({
      title: content['household-dependents-array-household-question'],
      labels: childHouseholdLabels,
      required: (formData, _index, fullData) => {
        const data = formData?.claimantRelationship ? formData : fullData;
        return isRelationshipToVeteran(data) === 'child';
      },
      hideIf: (formData, _index, fullData) => {
        const data = formData?.claimantRelationship ? formData : fullData;
        return isRelationshipToVeteran(data) !== 'child';
      },
    }),
    'view:livingSituation': {
      'ui:description': (
        <va-additional-info trigger="Why we ask about the child’s living situation">
          <p>
            The child’s living situation determines who receives their payment:
          </p>
          <ul>
            <li>
              <strong>If they live with you</strong>, the benefit is typically
              included in your payment
            </li>
            <li>
              <strong>If they live elsewhere</strong>, we may pay their guardian
              or the child directly to ensure the support reaches their
              household
            </li>
          </ul>
        </va-additional-info>
      ),
      'ui:options': {
        displayEmptyObjectOnReview: false,
      },
    },
  },
  schema: {
    type: 'object',
    required: [],
    properties: {
      nonChildLivesWith: yesNoSchema,
      childLivesWith: radioSchema(Object.keys(childHouseholdLabels)),
      'view:livingSituation': {
        type: 'object',
        properties: {},
      },
    },
  },
};

const childSupportPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['household-dependents-array-child-support-title'],
    ),
    childSupport: currencyUI(
      content['household-dependents-array-child-support-question'],
    ),
  },
  schema: {
    type: 'object',
    properties: {
      childSupport: currencySchema,
    },
    required: ['childSupport'],
  },
};

/** @returns {PageSchema} */
export const dependentsPages = arrayBuilderPages(options, pageBuilder => ({
  dependentsSummary: pageBuilder.summaryPage({
    title: content['household-dependents-array-summary-add'],
    path: 'household/dependents/add',
    depends: formData => formData.veteranChildrenCount > 0,
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  dependentName: pageBuilder.itemPage({
    title: content['household-dependents-array-child-name-title'],
    path: 'household/dependents/:index/name-and-information',
    depends: formData => formData.veteranChildrenCount > 0,
    uiSchema: namePage.uiSchema,
    schema: namePage.schema,
  }),
  dependentDobPlace: pageBuilder.itemPage({
    title: content['household-dependents-array-dob-place-title'],
    path: 'household/dependents/:index/date-and-place-of-birth',
    depends: formData => formData.veteranChildrenCount > 0,
    uiSchema: dobPlacePage.uiSchema,
    schema: dobPlacePage.schema,
  }),
  dependentRelationship: pageBuilder.itemPage({
    title: content['household-dependents-array-relationship-title'],
    path: 'household/dependents/:index/relationship-to-dependent',
    depends: formData => formData.veteranChildrenCount > 0,
    uiSchema: relationshipPage.uiSchema,
    schema: relationshipPage.schema,
  }),
  dependentInfo: pageBuilder.itemPage({
    title: content['household-dependents-array-dependent-info-title'],
    path: 'household/dependents/:index/information',
    depends: formData => formData.veteranChildrenCount > 0,
    uiSchema: dependentInfoPage.uiSchema,
    schema: dependentInfoPage.schema,
  }),
  dependentHousehold: pageBuilder.itemPage({
    title: content['household-dependents-array-household-title'],
    path: 'household/dependents/:index/household',
    depends: formData => formData.veteranChildrenCount > 0,
    uiSchema: householdPage.uiSchema,
    schema: householdPage.schema,
  }),
  dependentChildSupport: pageBuilder.itemPage({
    title: content['household-dependents-array-child-support-title'],
    path: 'household/dependents/:index/child-support',
    uiSchema: childSupportPage.uiSchema,
    schema: childSupportPage.schema,
    depends: (formData, index) =>
      (formData?.veteransChildren?.[index]?.childLivesWith === 'NO' ||
        formData?.veteransChildren?.[index]?.nonChildLivesWith === false) &&
      formData.veteranChildrenCount > 0,
  }),
}));

export default dependentsPages;
